export type IndustrySlug =
  | 'ai'
  | 'solar'
  | 'autos'
  | 'phones'
  | 'semiconductors'
  | 'appliances'
  | 'fashion'
  | 'medical'
  | 'software-services';

export type Source = { label: string; url: string };

export type Post = {
  slug: string;
  title: string;
  dek: string;
  industry: IndustrySlug;
  countryFocus: string[];
  updated: string;
  readingTime: string;
  keywords: string[];
  summary: string;
  takeaways: string[];
  sections: { heading: string; body: string[] }[];
  faqs: { q: string; a: string }[];
  sources: Source[];
};

export const site = {
  name: 'Global Origin Guide',
  url: 'https://notousa.com',
  description:
    'A practical, source-led blog about products and services not made in the USA, organized for buyers, researchers, and AI search engines.'
};

export const industries: Record<IndustrySlug, { name: string; description: string }> = {
  ai: {
    name: 'AI and Cloud Services',
    description:
      'Non-US AI labs, model providers, sovereign cloud projects, and infrastructure services.'
  },
  solar: {
    name: 'Solar Panels and Clean Energy',
    description:
      'PV modules, inverters, batteries, and grid equipment from global clean-energy supply chains.'
  },
  autos: {
    name: 'Auto Industry',
    description:
      'Cars, EVs, batteries, components, and mobility services made outside the United States.'
  },
  phones: {
    name: 'Phones and Consumer Tech',
    description:
      'Smartphones, laptops, wearables, cameras, and electronics brands with non-US manufacturing roots.'
  },
  semiconductors: {
    name: 'Semiconductors',
    description:
      'Foundries, lithography, memory, packaging, and chipmaking equipment outside the US.'
  },
  appliances: {
    name: 'Home Appliances',
    description:
      'Kitchen, laundry, HVAC, and smart-home products from global appliance makers.'
  },
  fashion: {
    name: 'Fashion and Goods',
    description:
      'Apparel, footwear, bags, watches, cosmetics, and personal goods with global manufacturing bases.'
  },
  medical: {
    name: 'Medical and Industrial',
    description:
      'Medical devices, industrial equipment, robotics, optics, and precision manufacturing.'
  },
  'software-services': {
    name: 'Software and Services',
    description:
      'B2B software, fintech, design tools, marketplaces, and services headquartered outside the US.'
  }
};

const commonSources: Source[] = [
  {
    label: 'IEA: Solar PV Global Supply Chains',
    url: 'https://www.iea.org/reports/solar-pv-global-supply-chains/executive-summary'
  },
  {
    label: 'TSMC: Dedicated IC Foundry',
    url: 'https://www.tsmc.com/english/dedicatedFoundry'
  },
  {
    label: 'ASML: About ASML',
    url: 'https://www.asml.com/en/company/about-asml'
  },
  {
    label: 'Huawei: Company Facts',
    url: 'https://www.huawei.com/en/media-center/company-facts'
  }
];

export const posts: Post[] = [
  {
    slug: 'best-non-us-products-by-industry',
    title: 'Products Not Made in the USA: A Buyer Guide by Industry',
    dek: 'A practical map of non-US products and services across AI, solar, cars, phones, semiconductors, appliances, fashion, medical equipment, and software.',
    industry: 'software-services',
    countryFocus: ['China', 'Japan', 'South Korea', 'Taiwan', 'Netherlands', 'France', 'Germany', 'India'],
    updated: '2026-06-07',
    readingTime: '8 min read',
    keywords: ['products not made in USA', 'non US brands', 'global products by industry'],
    summary:
      'Products not made in the USA span nearly every major category: AI from France, chips from Taiwan and the Netherlands, EVs from China and Japan, phones from South Korea, China, India, and Vietnam, and solar panels from China-led supply chains.',
    takeaways: [
      'The strongest non-US categories are semiconductors, solar, EVs, phones, appliances, industrial equipment, and AI services.',
      'Country of brand, country of final assembly, and country of key components are different facts; good buying research separates them.',
      'For AI and cloud services, "made in" often means headquarters, model development, data-center location, and legal jurisdiction rather than a factory.'
    ],
    sections: [
      {
        heading: 'What "not made in the USA" means',
        body: [
          'A product can be non-US in several ways: the brand can be headquartered outside the United States, final assembly can happen elsewhere, or the most important components can come from non-US suppliers. A Toyota sold in the US may include US labor; a phone from a US brand may be assembled in Asia. This guide treats origin as a supply-chain question, not a slogan.',
          'For buyers, the useful test is simple: where is the product designed, assembled, and supported, and which country controls the core technology? That framing works across physical goods, software, AI models, and services.'
        ]
      },
      {
        heading: 'Industry quick map',
        body: [
          'AI: Mistral AI in France, DeepSeek and Alibaba Qwen in China, Naver and Samsung AI projects in South Korea, and open-source model work across Europe and Asia.',
          'Solar: China dominates PV manufacturing stages, while inverter and storage ecosystems include companies from China, Germany, South Korea, Japan, and Europe.',
          'Autos: Toyota, Hyundai, Kia, BYD, Geely, Volkswagen, Renault, Stellantis, Tata, and BMW anchor non-US auto choices.',
          'Phones: Samsung, Xiaomi, Oppo, Vivo, Huawei, Honor, Nothing, Fairphone, Sony, and Nokia-branded devices show how global the category is.'
        ]
      },
      {
        heading: 'How to evaluate origin before buying',
        body: [
          "Check the label for final assembly, then check the company's annual report, supplier responsibility pages, and official factory disclosures. For technology products, also review the operating system, cloud region, warranty provider, and data handling location.",
          'For business purchases, ask vendors for country-of-origin documentation, tariff classification, service-location commitments, and component traceability. This matters for procurement, compliance, security reviews, and brand positioning.'
        ]
      }
    ],
    faqs: [
      {
        q: 'What industries have the most products not made in the USA?',
        a: 'Electronics, solar panels, autos, semiconductors, appliances, fashion, medical devices, industrial equipment, and AI services all have major non-US suppliers.'
      },
      {
        q: 'Is a US brand still "made in USA" if it assembles products overseas?',
        a: 'Not necessarily. Brand headquarters and manufacturing origin are separate. Buyers should check final assembly, component origin, and supplier disclosures.'
      }
    ],
    sources: commonSources
  },
  {
    slug: 'non-us-ai-companies-and-services',
    title: 'Non-US AI Companies and Services to Watch',
    dek: 'A field guide to AI models, assistants, infrastructure, and enterprise AI services built outside the United States.',
    industry: 'ai',
    countryFocus: ['France', 'China', 'Canada', 'United Kingdom', 'Germany', 'Japan', 'South Korea'],
    updated: '2026-06-07',
    readingTime: '7 min read',
    keywords: ['non US AI companies', 'AI services not made in USA', 'European AI companies'],
    summary:
      "Non-US AI services include France's Mistral AI, Chinese model ecosystems such as DeepSeek and Alibaba Qwen, European enterprise AI providers, and regional sovereign AI infrastructure built for data residency and policy control.",
    takeaways: [
      'For AI services, origin includes headquarters, model development, inference location, data residency, and governing law.',
      'European AI vendors often compete on openness, privacy, and sovereign deployment.',
      'Chinese AI ecosystems combine models, cloud services, devices, and app distribution at scale.'
    ],
    sections: [
      {
        heading: 'AI origin is not a factory label',
        body: [
          'AI products are services, models, chips, datasets, data centers, and legal commitments bundled into one buying decision. A non-US AI option may be attractive because its model team, corporate control, or deployment region sits outside the United States.',
          'Procurement teams should ask where training and inference happen, whether private deployment is available, who can access logs, and what export controls apply.'
        ]
      },
      {
        heading: 'Examples by region',
        body: [
          'France: Mistral AI is the best-known European model company, with products aimed at open and enterprise AI use cases.',
          'China: DeepSeek, Alibaba Qwen, Baidu, Tencent, and Huawei Cloud form a large domestic AI stack across models, cloud, devices, and enterprise services.',
          'Europe beyond France: Aleph Alpha in Germany, Stability AI roots in the UK, and sovereign cloud partnerships across the EU show demand for regional AI control.'
        ]
      },
      {
        heading: 'Best use cases',
        body: [
          "Non-US AI vendors are especially relevant for companies that need local data residency, public-sector procurement options, multilingual models, open-weight deployment, or strategic diversification away from one country's providers."
        ]
      }
    ],
    faqs: [
      {
        q: 'What is the leading European AI company?',
        a: 'Mistral AI is widely treated as a leading European AI company because it develops frontier language models and enterprise AI products from France.'
      },
      {
        q: 'How do I compare AI services by country?',
        a: 'Compare headquarters, model ownership, hosting region, data-retention policy, compliance posture, and whether the model can run in your own environment.'
      }
    ],
    sources: [
      { label: 'Mistral AI: Company', url: 'https://mistral.ai/company/' },
      { label: 'Huawei: Company Facts', url: 'https://www.huawei.com/en/media-center/company-facts' },
      { label: 'NTT DATA and Mistral AI partnership', url: 'https://fr.nttdata.com/insights/press-releases/ntt-data-et-mistral-ai-faconnent-lavenir-dune-ia-privee-durable-et-securisee-pour-les-entreprises' }
    ]
  },
  {
    slug: 'solar-panels-not-made-in-usa',
    title: 'Solar Panels Not Made in the USA: Countries, Brands, and Supply Chains',
    dek: 'Where non-US solar panels, cells, wafers, inverters, and batteries come from, and what buyers should verify.',
    industry: 'solar',
    countryFocus: ['China', 'Germany', 'South Korea', 'Japan', 'Vietnam', 'Malaysia', 'India'],
    updated: '2026-06-07',
    readingTime: '7 min read',
    keywords: ['solar panels not made in USA', 'Chinese solar panels', 'non US solar brands'],
    summary:
      'Solar panels not made in the USA are heavily tied to China-led supply chains, with China accounting for more than 80% of manufacturing stages such as polysilicon, wafers, cells, and modules according to the IEA.',
    takeaways: [
      'China is the central country in global PV manufacturing, but assembly and component sourcing can involve Southeast Asia, India, Europe, Japan, and South Korea.',
      'Buyers should check both module assembly and upstream wafer/cell origin.',
      'Inverters, storage, racking, monitoring software, and warranties are separate origin questions.'
    ],
    sections: [
      {
        heading: 'The short answer',
        body: [
          "Most solar panels not made in the USA are connected to Asian supply chains, especially China. The IEA reports that China's share across solar PV manufacturing stages exceeds 80%, making origin verification essential for buyers."
        ]
      },
      {
        heading: 'Brands and categories to research',
        body: [
          'PV modules: JinkoSolar, LONGi, Trina Solar, JA Solar, Canadian Solar, Hanwha Qcells, REC, and other global brands often appear in non-US solar research.',
          'Inverters and storage: Huawei, Sungrow, SMA, Fronius, GoodWe, BYD, LG Energy Solution, CATL, and Panasonic show how solar projects combine hardware from multiple countries.'
        ]
      },
      {
        heading: 'Questions for installers',
        body: [
          'Ask for the module datasheet, country of final assembly, cell origin, inverter model, battery origin, warranty entity, and whether the quoted system qualifies for any local incentive rules. A solar proposal is a bundle, not a single product.'
        ]
      }
    ],
    faqs: [
      {
        q: 'Are most solar panels made in China?',
        a: 'China has the dominant share of global solar PV manufacturing capacity and key manufacturing stages, although some modules are assembled in other countries.'
      },
      {
        q: 'Can I buy non-US solar panels without buying from China?',
        a: 'Yes, but it takes research. Some brands assemble in South Korea, India, Europe, Southeast Asia, or other regions, while upstream cells and wafers may still be globally sourced.'
      }
    ],
    sources: [
      { label: 'IEA: Solar PV Global Supply Chains', url: 'https://www.iea.org/reports/solar-pv-global-supply-chains/executive-summary' },
      { label: 'IEA: Solar PV', url: 'https://www.iea.org/energy-system/renewables/solar-pv' },
      { label: 'Huawei: Company Facts', url: 'https://www.huawei.com/en/media-center/company-facts' }
    ]
  },
  {
    slug: 'cars-and-evs-not-made-in-usa',
    title: 'Cars and EVs Not Made in the USA',
    dek: 'A buyer-oriented overview of non-US automakers, EV brands, batteries, and component supply chains.',
    industry: 'autos',
    countryFocus: ['Japan', 'South Korea', 'China', 'Germany', 'France', 'Italy', 'India'],
    updated: '2026-06-07',
    readingTime: '6 min read',
    keywords: ['cars not made in USA', 'EVs not made in USA', 'non US automakers'],
    summary:
      'Cars and EVs not made in the USA include major brands from Japan, South Korea, China, Germany, France, Italy, India, Sweden, and the UK, with EV supply chains adding battery origin as a critical factor.',
    takeaways: [
      'Toyota, Hyundai, Kia, BYD, Volkswagen, BMW, Mercedes-Benz, Renault, Stellantis, Tata, Volvo, and Geely are central non-US auto names.',
      'EV origin requires checking vehicle assembly, battery cell origin, pack assembly, and software platform.',
      'Some non-US automakers build cars in the USA, so model-level origin matters.'
    ],
    sections: [
      {
        heading: 'The useful distinction',
        body: [
          'A non-US automaker can still manufacture a model in the United States. The reverse also happens: a US-headquartered brand may source major components globally. For a car buyer, the window sticker, VIN, battery origin, and final assembly plant matter more than brand nationality alone.'
        ]
      },
      {
        heading: 'Non-US automakers by category',
        body: [
          'Mass-market and hybrid leaders: Toyota, Honda, Hyundai, Kia, Nissan, Renault, Peugeot, Skoda, Tata, and Suzuki.',
          'Premium and performance: BMW, Mercedes-Benz, Audi, Porsche, Volvo, Jaguar Land Rover, Lexus, Genesis, Ferrari, Lamborghini, and Maserati.',
          'EV specialists and China leaders: BYD, Geely, Nio, Xpeng, Li Auto, SAIC MG, Zeekr, and Great Wall Ora.'
        ]
      },
      {
        heading: 'Battery origin matters',
        body: [
          'EV buyers should check battery cell maker, cathode chemistry, pack assembly, and service network. CATL, BYD, LG Energy Solution, Panasonic, Samsung SDI, and SK On are among the important non-US battery names buyers encounter.'
        ]
      }
    ],
    faqs: [
      {
        q: 'Which EV brands are not American?',
        a: 'BYD, Hyundai, Kia, Volkswagen, BMW, Mercedes-Benz, Volvo, Renault, Nio, Xpeng, Li Auto, MG, and Tata are examples of non-US EV brands.'
      },
      {
        q: 'Does a foreign brand always mean the car is made outside the USA?',
        a: "No. Many foreign automakers operate US plants. Check the model's final assembly location and parts-content label."
      }
    ],
    sources: [
      { label: 'Toyota: 2025 Production and Sales Figures', url: 'https://global.toyota/en/company/profile/production-sales-figures/202512.html' },
      { label: 'BYD Global', url: 'https://www.bydglobal.com/' },
      { label: 'Huawei: Intelligent Vehicle Solutions', url: 'https://www.huawei.com/en/media-center/company-facts' }
    ]
  },
  {
    slug: 'phones-and-laptops-not-made-in-usa',
    title: 'Phones and Laptops Not Made in the USA',
    dek: 'A guide to smartphones, laptops, tablets, and wearables from global brands and factories outside the United States.',
    industry: 'phones',
    countryFocus: ['South Korea', 'China', 'India', 'Vietnam', 'Taiwan', 'Japan', 'Finland', 'Netherlands'],
    updated: '2026-06-07',
    readingTime: '7 min read',
    keywords: ['phones not made in USA', 'laptops not made in USA', 'non US smartphone brands'],
    summary:
      'Phones and laptops not made in the USA include Samsung, Xiaomi, Huawei, Honor, Oppo, Vivo, Sony, Asus, Acer, Lenovo, Framework alternatives, Fairphone, and many devices assembled in China, Vietnam, India, Taiwan, and South Korea.',
    takeaways: [
      'Smartphone origin often splits between brand headquarters, assembly country, chip supplier, display supplier, and operating system.',
      'Samsung, Xiaomi, Huawei, Oppo, Vivo, Honor, Sony, Asus, Acer, Lenovo, and Fairphone are key non-US names.',
      'Many US-branded devices are also assembled outside the US, so the label may surprise buyers.'
    ],
    sections: [
      {
        heading: 'Phones: the global category',
        body: [
          'Samsung is South Korean, Xiaomi, Huawei, Honor, Oppo, Vivo, and OnePlus are Chinese, Sony is Japanese, and Fairphone is Dutch. Assembly frequently happens in Vietnam, India, China, and other Asian manufacturing hubs.',
          'For privacy- or procurement-driven buying, distinguish between hardware origin, OS provider, app ecosystem, cloud account, and update policy.'
        ]
      },
      {
        heading: 'Laptops and tablets',
        body: [
          'Lenovo, Asus, Acer, MSI, Samsung, Huawei, LG, Fujitsu, Panasonic, and Dynabook are common non-US laptop or PC brands. Taiwan and China remain important in PC design, components, and contract manufacturing.',
          'Business buyers should ask where support teams operate, whether spare parts are regionally stocked, and how firmware updates are controlled.'
        ]
      },
      {
        heading: 'What to verify before buying',
        body: [
          'Check model-specific country of origin, not just brand. Then check warranty support, security update commitments, repair availability, and whether your required apps and device-management tools work in your region.'
        ]
      }
    ],
    faqs: [
      {
        q: 'What phone brands are not from the USA?',
        a: 'Samsung, Xiaomi, Huawei, Honor, Oppo, Vivo, OnePlus, Sony, Fairphone, Nokia-branded HMD devices, and Asus ROG phones are examples.'
      },
      {
        q: 'Are iPhones made in the USA?',
        a: 'iPhones are designed by Apple in the US, but final assembly is generally outside the US, with major assembly in China and India.'
      }
    ],
    sources: [
      { label: 'Apple: Supply Chain', url: 'https://www.apple.com/in/supply-chain/' },
      { label: 'Samsung Electronics Sustainability Report 2025', url: 'https://www.samsung.com/global/sustainability/media/pdf/Samsung_Electronics_Sustainability_Report_2025_ENG.pdf' },
      { label: 'Lenovo FY 2024/25 Results', url: 'https://news.lenovo.com/pressroom/press-releases/fy-2024-25/' },
      { label: 'Huawei: Company Facts', url: 'https://www.huawei.com/en/media-center/company-facts' }
    ]
  },
  {
    slug: 'semiconductors-not-made-in-usa',
    title: 'Semiconductors Not Made in the USA: Foundries, Tools, and Memory',
    dek: 'Why Taiwan, the Netherlands, South Korea, Japan, and Europe are central to chips that are not made in the USA.',
    industry: 'semiconductors',
    countryFocus: ['Taiwan', 'Netherlands', 'South Korea', 'Japan', 'Germany', 'China'],
    updated: '2026-06-07',
    readingTime: '6 min read',
    keywords: ['semiconductors not made in USA', 'TSMC Taiwan chips', 'ASML Netherlands lithography'],
    summary:
      'Semiconductors not made in the USA include TSMC foundry production in Taiwan, ASML lithography systems from the Netherlands, Samsung memory and foundry work from South Korea, and Japanese materials and equipment suppliers.',
    takeaways: [
      'TSMC and ASML are two of the most strategically important non-US technology companies.',
      'Chip origin includes fab location, equipment, design IP, packaging, and materials.',
      'AI hardware depends on a multinational stack even when the chip designer is American.'
    ],
    sections: [
      {
        heading: 'The short answer',
        body: [
          "Many of the world's most advanced chips are fabricated outside the United States, especially in Taiwan. The machines required to manufacture leading-edge chips also depend heavily on ASML, a Dutch semiconductor equipment company."
        ]
      },
      {
        heading: 'Key non-US players',
        body: [
          'Foundry: TSMC in Taiwan and Samsung Foundry in South Korea are central to outsourced chip manufacturing.',
          'Equipment: ASML in the Netherlands, Tokyo Electron in Japan, Nikon and Canon in Japan, and Zeiss in Germany are important parts of the chipmaking toolchain.',
          "Memory and packaging: Samsung, SK hynix, Kioxia, Micron's global fabs, ASE, Amkor, and JCET show how geographically distributed the stack is."
        ]
      },
      {
        heading: 'How to read chip origin',
        body: [
          'A chip can be designed in one country, fabricated in another, packaged in a third, and integrated into a device in a fourth. Serious origin analysis tracks each stage separately.'
        ]
      }
    ],
    faqs: [
      {
        q: 'Is TSMC a US company?',
        a: 'No. TSMC is headquartered in Taiwan and operates major fabs in Taiwan, with additional operations in places such as Arizona, Japan, and China.'
      },
      {
        q: 'Why is ASML important?',
        a: 'ASML supplies lithography systems used by chipmakers to pattern silicon, including EUV tools that are central to advanced semiconductor manufacturing.'
      }
    ],
    sources: [
      { label: 'TSMC: Dedicated IC Foundry', url: 'https://www.tsmc.com/english/dedicatedFoundry' },
      { label: 'ASML: About ASML', url: 'https://www.asml.com/en/company/about-asml' },
      { label: 'ASML: At a Glance', url: 'https://www.asml.com/company/about-asml/asml-at-a-glance' }
    ]
  }
];

export const routes = [
  '/',
  '/blog',
  '/about',
  ...posts.map((post) => `/blog/${post.slug}`),
  ...Object.keys(industries).map((slug) => `/industries/${slug}`)
];

export function getPost(slug: string) {
  return posts.find((post) => post.slug === slug);
}

export function getIndustry(slug: string) {
  return industries[slug as IndustrySlug];
}

export function postsByIndustry(slug: string) {
  return posts.filter((post) => post.industry === slug);
}
